import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { isEmpty } from 'lodash';
import ContentEditor from 'components/ContentEditor';
import { ContentPage } from 'views/App/Portal/Policy/DraftManuals/Detail/styles';
import { createStandard } from '../../../../_data_sources/AccreditationStandards/manageStandard';
import { translate as t } from 'hooks/useTranslations';
import type {
  AccreditationStandard,
  AccreditationStandardNew,
  AccreditationStandardList,
} from '../../../../_types/awb';
import { useStandardsContext } from '../context';
import { useQueryClient } from 'react-query';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import StandardsList from '../List';
import StandardActions from '../_components/StandardActions';
import useAccreditingBodyVersion from '../../../../_data_sources/AccreditationVersions/useAccreditationVersion';
import { getStandard } from '../../../../_data_sources/AccreditationStandards/useStandard';
import { isStandard, isSubStandard, isRequirement } from '../../../../_helpers/awbHelper';
import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import errorHelper from 'helpers/errorHelper';
import { type ServerErrorResponse } from 'data_sources/constants';

const NewStandard: React.FC = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { getNextPosition } = useStandardsContext();
  const queryClient = useQueryClient();
  const { id: accreditationId, versionId, standardId: parentStandardId } = useParams();
  const versionQuery = useAccreditingBodyVersion(accreditationId!, versionId);
  const [parentStandard, setParentStandard] = useState<AccreditationStandard | null>(null);
  const headingInputRef = useRef<HTMLInputElement>(null);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  useEffect(() => {
    if (parentStandardId) {
      getStandard(accreditationId!, versionId!, parentStandardId).then(setParentStandard).catch(console.error);
    }
  }, [accreditationId, versionId, parentStandardId]);

  useEffect(() => {
    // Focus on heading input after a short delay
    const timer = setTimeout(() => {
      headingInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const [heading, setHeading] = useState('');
  const [title, setTitle] = useState('New standard');
  const [draftContent, setDraftContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const standardType = useMemo(() => {
    if (!parentStandardId) {
      return 'standard';
    }

    if (parentStandard) {
      if (isStandard(parentStandard)) {
        return 'subStandard';
      }
      if (isSubStandard(parentStandard)) {
        return 'requirement';
      }
    }

    return 'standard';
  }, [parentStandardId, parentStandard]);

  const standardTypeText = useMemo(() => {
    switch (standardType) {
      case 'standard':
        return t('awb.standards.new.standard');
      case 'subStandard':
        return t('awb.standards.new.subStandard');
      case 'requirement':
        return t('awb.standards.new.requirement');
      default:
        return t('awb.standards.new.standard');
    }
  }, [standardType]);

  const handleStandardSelection = (newStandardId: string) => {
    navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${newStandardId}`);
  };

  const handleAddStandard = (parentId?: string) => {
    if (parentId) {
      navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${parentId}/new`);
    } else {
      navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/new`);
    }
  };

  const handleSaveStandard = async () => {
    setIsSaving(true);
    try {
      const standardData: AccreditationStandardNew = {
        heading,
        title,
        position: getNextPosition(parentStandardId),
        draftContent: ContentConversionHelper.contentForRender(draftContent),
        parentStandardId: parentStandardId || '',
        publishedContent: '',
        status: 'ENABLED',
      };

      const newlyCreated: any = await createStandard(accreditationId!, versionId!, standardData);
      if (newlyCreated?.id) {
        await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
        await queryClient.invalidateQueries(['accreditation_standard', accreditationId, versionId, newlyCreated.id]);

        if (newlyCreated.parentStandardId) {
          await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
        }

        navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${newlyCreated.id}`);
      }
    } catch (err) {
      console.error('Error creating standard:', err);
      setServerErrors(err as ServerErrorResponse);
    }
    setIsSaving(false);
  };

  const isFormValid = useMemo(() => {
    return !isEmpty(heading) && !isEmpty(title);
  }, [heading, title]);

  const renderContent = () => (
    <ContentPage>
      <Stack spacing={4}>
        {errorHelper.renderAllErrors(serverErrors)}
        <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
        <Box>
          <Box sx={{ display: 'flex', width: '100%', gap: 2, mb: 4 }}>
            <TextField
              inputRef={headingInputRef}
              placeholder='Enter #'
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              sx={{ width: '15%' }}
            />
            <TextField
              placeholder='Enter Standard Title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ width: '85%' }}
            />
          </Box>
          <Stack spacing={3}>
            <ContentEditor
              id='content_new'
              key='content_new'
              contentHTML={draftContent}
              onSave={setDraftContent}
              isEditable
              trackChangesEnabled={false}
              textEntitiesEnabled={false}
              crossReferencesEnabled={false}
              imagesEnabled={false}
              attachmentsEnabled={false}
            />
          </Stack>
        </Box>
      </Stack>
    </ContentPage>
  );

  return (
    <TakeoverLayout
      pageTitle={versionQuery?.data?.version || ''}
      backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/`}
      menubarLeft={<StandardActions />}
      leftDrawer={
        <StandardsList
          selectedStandard=''
          setSelectedStandard={handleStandardSelection}
          onAddStandard={handleAddStandard}
          placeholderStandard={{
            id: 'new-standard',
            title,
            heading: heading || '',
            parentStandardId: parentStandardId || undefined,
            position: 1,
            standardCount: 0,
            status: 'DRAFT',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }}
          initialExpandedItems={parentStandardId ? [parentStandardId] : []}
        />
      }
      contentHeaderRight={
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={handleSaveStandard}
          disabled={!isFormValid || isSaving}
        >
          {t('awb.standards.new.save')}
        </Button>
      }
      contentBackground='neutral'
      rightDrawer={{
        header: 'Written Directives',
        content: <></>,
      }}
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default NewStandard;
